import {Container, Graphics, isMobile, Sprite, Texture} from "pixi.js";
import {Reel} from "./reel";
import {gsap} from "gsap";
import {CANVAS, SYMBOLS} from "../constants/configs";
import {getDurations, setComingSpinQuickFall, getStores} from "./spin";
import {DURATIONS} from "../constants/durations";
import {ExplodeWinAmount} from "./explodeWinAmount";
import {universalClick} from "../utilites";
import {MultipliersBoard} from "./multipliersBoard";
import {TumbleWinBoard} from "./tumbleWinBoard";
import {pixiComponents} from "../index";
import {emptyBoxSound, explosionSound, reelStopSound} from "../soundService";

export class SlotBoard {
    reels = [];
    explodeWinAmountItems = [];
    container = new Container();
    reelsContainer = new Container();
    reelContainerMask = new Graphics();
    backgroundContainer = new Container();
    multipliersBoard = null;
    cleanUpPromise;
    resolveCleanUpPromise;

    constructor({width, height, rowCount, reelCount, reelConfigs,}) {
        this.width = width;
        this.height = height;
        this.rowCount = rowCount;
        this.reelCount = reelCount;
        this.reelConfigs = reelConfigs;

        if(isMobile.phone){
            this.container.scale.set(0.65);
        }

        this.createBackground();
        this.createTumbleWinBoard();
        this.createReelsContainer();
        this.createMultipliersBoard();
        this.createReelContainerMask();
        this.containerSetup();
    }

    containerSetup() {
        this.container.position.set((CANVAS.width-this.container.width)/2, 43);
        this.container.eventMode = "static";
        universalClick(this.container, setComingSpinQuickFall);
        this.updateContainerPosition()
    }

    updateContainerPosition() {
        if (!isMobile.phone) {
            this.container.position.set(300, 48);
            return;
        }
        if (getStores().store.isHorizontalScreen) {
            this.container.position.set(203, 30);
            this.tumbleWinBoard.container.position.set(this.backgroundContainer.width / 2, 0);
            this.tumbleWinBoard.container.scale.set(1);
        } else {
            this.container.position.set(7, 65);
            this.tumbleWinBoard.container.position.set(this.backgroundContainer.width / 2, -20);
            this.tumbleWinBoard.container.scale.set(1.3);
        }
    }

    createTumbleWinBoard() {
        this.tumbleWinBoard = new TumbleWinBoard();
        this.container.addChild(this.tumbleWinBoard.container);
        this.tumbleWinBoard.container.position.set(this.backgroundContainer.width / 2, 0);
    }

    createBackground() {
        const boardSprite = new Sprite(Texture.from('./assets/board-bg.webp'));
        this.backgroundContainer.addChild(boardSprite);
        this.container.addChild(this.backgroundContainer);
        boardSprite.anchor.set(0.01, 0.018);

        this.setBonusBackground = () => boardSprite.texture = Texture.from('./assets/blue-board-bg.webp');
        this.setInitialBackground = () => boardSprite.texture = Texture.from('./assets/board-bg.webp');
    }

    createReelsContainer() {
        for (let i = 0; i < this.reelCount; i++) {
            const reel = new Reel({...this.reelConfigs, parentBoard: this});
            reel.container.x = (this.width / this.reelCount * i) + this.width / this.reelCount / 2;
            reel.container.y = (this.height - this.reelConfigs.height) / 2;
            this.reels.push(reel);
            this.reelsContainer.addChild(reel.container);
        }
        this.container.addChild(this.reelsContainer);
    }

    createMultipliersBoard() {
        this.multipliersBoard = new MultipliersBoard();
        this.reelsContainer.addChild(this.multipliersBoard.container);
    }

    createExplodeWinAmount(index) {
        const explodeWinAmountItem = new ExplodeWinAmount();
        this.explodeWinAmountItems[index] = explodeWinAmountItem;
        this.container.addChild(explodeWinAmountItem.pixiText);
    }

    createReelContainerMask() {
        this.reelContainerMask.beginFill('rgba(0,0,0,0.01)');
        this.reelContainerMask.drawRect(-this.reelsContainer.width / 2, this.reelsContainer.height * 0.01, this.reelsContainer.width * 2, this.height * 0.98);
        this.reelContainerMask.endFill();
        this.reelContainerMask.visible = false;
        this.reelsContainer.addChild(this.reelContainerMask);
    }

    setReelContainerMask() {
        this.reelContainerMask.visible = true;
        this.reelsContainer.mask = this.reelContainerMask;
    }

    removeReelContainerMask() {
        this.reelContainerMask.visible = false;
        this.reelsContainer.mask = null;
    }

    clear() {
        this.cleanUpPromise = new Promise((resolve) => {
            this.resolveCleanUpPromise = () => {
                this.cleanUpPromise = null;
                resolve();
            };
            let completed = 0;
            emptyBoxSound.play();
            this.setReelContainerMask();
            const clearSymbolsDelay = getDurations(DURATIONS.DURATION_NAMES.clearSymbolsDelay);
            const clearSymbolDuration = getDurations(DURATIONS.DURATION_NAMES.clearSymbolDuration);
            this.reels.forEach((reel, index) =>
                reel.gsapAnimation = gsap.delayedCall(index * getDurations(DURATIONS.DURATION_NAMES.clearReelsDelay), () => reel.clear(clearSymbolsDelay, clearSymbolDuration).then(() => {
                    completed += 1;
                    if (completed >= this.reelCount) {
                        this.cleanUpPromise = null;
                        resolve();
                    }
                }))
            )
        });
        return this.cleanUpPromise;
    }

    killGsapAnimations() {
        this.reels.forEach((reel, index) => {
            reel.gsapAnimation?.kill();
            reel.killSymbolsGsapAnimation();
        });
    }

    async throw(cards) {
        if (getStores().spinStore.getIsTurbo() || getStores().spinStore.isQuickFall) {
            reelStopSound.play();
        }

        return new Promise((resolve) => {
            let throwAnimationCompletedReelsCount = 0;
            this.reels.forEach((reel, index) =>
                gsap.delayedCall(index * getDurations(DURATIONS.DURATION_NAMES.throwReelsDelay), () => reel.throw(cards[index]).then(() => {
                    throwAnimationCompletedReelsCount += 1;
                    if (throwAnimationCompletedReelsCount === this.reels.length) {
                        this.removeReelContainerMask();
                        resolve();
                    }
                }))
            )
        })
    }

    setSymbols(slotBoardSymbols) {
        this.reels.forEach((reel, index) =>
            reel.setSymbols(slotBoardSymbols[index])
        )
    }

    highlightExplodeWinAmount(explodingIconId, explodingSessionInfo, explodeWinAmountItemIndex) {
        const {cards, amount, odds} = explodingSessionInfo;
        const position = {x: 0, y: 0};
        const selected = {reelIndex: 0, rowIndex: 0};
        const reelIndexesWhichContainExplodingIcon = [];
        const rowIndexesWhichContainExplodingIcon = [];
        const winAmount = (amount * odds[explodingIconId]).toFixed(2);

        cards.forEach((reel, reelIndex) => {
            for (let i = 0; i < reel.length; i++) {
                if (reel[i] === explodingIconId) {
                    reelIndexesWhichContainExplodingIcon.push(reelIndex);
                    break;
                }
            }
        })
        selected.reelIndex = reelIndexesWhichContainExplodingIcon[Math.floor(reelIndexesWhichContainExplodingIcon.length / 2)];

        for (let i = 0; i < cards[selected.reelIndex].length; i++) {
            if (cards[selected.reelIndex][i] === explodingIconId) {
                rowIndexesWhichContainExplodingIcon.push(i);
            }
        }
        selected.rowIndex = rowIndexesWhichContainExplodingIcon[Math.floor(rowIndexesWhichContainExplodingIcon.length / 2)];

        position.x = (this.width / this.reelCount * selected.reelIndex) + this.width / this.reelCount / 2;
        position.y = (this.height / this.rowCount * selected.rowIndex) + this.height / this.rowCount / 2;

        if (this.explodeWinAmountItems[explodeWinAmountItemIndex]) {
            this.explodeWinAmountItems[explodeWinAmountItemIndex].highlight(position.x, position.y, winAmount);
        } else {
            this.createExplodeWinAmount(explodeWinAmountItemIndex);
            this.explodeWinAmountItems[explodeWinAmountItemIndex].highlight(position.x, position.y, winAmount);
        }
    }

    explodeSession(sessionInfo) {
        return new Promise((resolve) => {
            const {cards, explodedSymbols, newSymbols, winAmount} = sessionInfo;
            if (sessionInfo.winAmount) {
                explosionSound.play();
            }
            this.setSymbols(cards);
            const explodingIconsNames = Object.keys(explodedSymbols).map(num => SYMBOLS[num]);
            let explodedReelCount = 0;
            this.setReelContainerMask();
            this.reels.forEach((reel, index) => {
                reel.explodeSymbolsByName(explodingIconsNames, newSymbols[index])
                    .then(async () => {
                        await reel.condenseSymbols();
                        explodedReelCount += 1;
                        if (explodedReelCount === this.reels.length) {
                            this.removeReelContainerMask();
                            resolve();
                        }
                    })
            });

            gsap.delayedCall(0.4, () => {
                Object.keys(explodedSymbols).forEach((iconId, index) => this.highlightExplodeWinAmount(parseInt(iconId, 10), sessionInfo, index));
            })

        })

    }

    highlightIcon(iconName) {
        return new Promise((resolve) => {
            const highlightingInfo = {
                count: 0,
                completedHighlighting: 0,
            };

            this.reels.forEach((reel) => {
                reel.symbols.forEach((symbol) => {
                    if (symbol.iconName === iconName) {
                        highlightingInfo.count++;
                        symbol.playHighlightingAnimation().then(() => {
                            highlightingInfo.completedHighlighting++;
                            if (highlightingInfo.completedHighlighting === highlightingInfo.count) {
                                resolve();
                            }
                        })
                    }
                })
            })
        })
    }

}